import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { getUnprocessableErrors } from 'utils/error'
import { Button } from 'components/v2/Button'
import { Select } from 'components/v2/Select'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { Textarea } from 'components/v2/Textarea'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { SelectTags } from 'components/v2/SelectTags'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { classMessages } from 'features/Lines/components/Class/messages'
import { CLASSES } from 'features/Lines/components/Class/constants'
import { amenitiesMessages } from 'features/Lines/components/Amenities/messages'
import { AMENITIES } from 'features/Lines/components/Amenities/constants'
import { newLineSchema } from './schema'

const messages = defineMessages({
  namePlaceholder: {
    id: 'lines.new.form.name.placeholder',
    defaultMessage: 'Name'
  },
  refundRulesPlaceholder: {
    id: 'lines.new.form.refundRules.placeholder',
    defaultMessage: 'Tell us more about the refunding rules.'
  },
  selectClass: {
    id: 'lines.new.form.class.select',
    defaultMessage: 'Choose class'
  }
})

const CLASSES_LIST = [CLASSES.ECONOMICO, CLASSES.PRIMERA, CLASSES.LUJO]

const AMENITIES_LIST = [
  AMENITIES.WIFI,
  AMENITIES.TV,
  AMENITIES.ETICKET,
  AMENITIES.WC,
  AMENITIES.AC,
  AMENITIES.CHAIR,
  AMENITIES.PLUG,
  AMENITIES.LIGHT,
  AMENITIES.EXCURSION_TRIP,
  AMENITIES.LUNCHBOX,
  AMENITIES.GUIDE
]

const getFilteredAmenities = (values: TSFixMe): TSFixMe => {
  return AMENITIES_LIST.filter((amenity): TSFixMe => {
    return values.indexOf(amenity) === -1
  })
}

const NewLineForm = (props: TSFixMe): TSFixMe => {
  const { values, touched, errors, handleChange, handleBlur, handleSubmit, error, loading, setFieldValue, s3Error } =
    props

  const intl = useIntl()
  const [filteredAmenities, setFilteredAmenities] = useState(getFilteredAmenities(values.amenities))

  const handleFileChange = (file: TSFixMe): void => {
    setFieldValue('logo', file)
  }

  const handleAmenitiesChange = (values: TSFixMe): TSFixMe => {
    setFieldValue('amenities', values)
    setFilteredAmenities(getFilteredAmenities(values))
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column>
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputFile
              value={values.logo}
              label={<FormattedMessage id="dictionary.logo" defaultMessage="Logo" />}
              placeholder={<FormattedMessage id="lines.new.logo.placeholder" defaultMessage="your logo" />}
              onChange={handleFileChange}
              touched={touched.logo}
              errors={errors.logo}
            />
          </Column>
        </Row>
        {values.logo && (
          <Row>
            <Column width="50%">
              <FilePreview file={values.logo} onRemoveFile={(): void => handleFileChange('')} />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="40%">
            <Select
              id="class"
              value={values.class}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.class}
              errors={errors.class}
              label={<FormattedMessage id="dictionary.class" defaultMessage="Class" />}
            >
              <option value="">{intl.formatMessage(messages.selectClass)}</option>

              {CLASSES_LIST.map((lineClass): TSFixMe => {
                return (
                  <option value={lineClass} key={lineClass}>
                    {intl.formatMessage(classMessages[lineClass])}
                  </option>
                )
              })}
            </Select>
          </Column>
          <Column width="60%">
            <SelectTags
              id="amenities"
              values={values.amenities}
              items={filteredAmenities}
              getTagLabel={(value): TSFixMe => {
                return intl.formatMessage(amenitiesMessages[value])
              }}
              onChange={handleAmenitiesChange}
              onBlur={handleBlur}
              label={<FormattedMessage id="lines.new.form.amenities.label" defaultMessage="Amenities (Optional)" />}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Textarea
              id="refundRules"
              value={values.refundRules}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.refundRules}
              errors={errors.refundRules}
              placeholder={intl.formatMessage(messages.refundRulesPlaceholder)}
              label={<FormattedMessage id="dictionary.refundingRules" defaultMessage="Refunding rules" />}
            />
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {!isEmpty(s3Error) && (
          <div>
            <ErrorMessage>{s3Error}</ErrorMessage>
          </div>
        )}
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              {props.isEdit ? (
                <FormattedMessage
                  id="lines.edit.form.error"
                  defaultMessage="There was an error while updating your line"
                />
              ) : (
                <FormattedMessage
                  id="lines.new.form.error"
                  defaultMessage="There was an error while creating your line"
                />
              )}
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to={props.isEdit ? '/lines' : '/companies'} kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {props.isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewLine = ({ line = {}, onSubmit, error, loading, isEdit, s3Error }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialLine = {
    name: line.name ? line.name : '',
    amenities: line.amenities ? line.amenities : [],
    refundRules: line.refundRules ? line.refundRules : '',
    class: line.class ? line.class : '',
    type: 'BUS',
    logo: line.logoPath ? line.logoPath : ''
  }

  return (
    <Formik initialValues={initialLine} validationSchema={newLineSchema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return (
          <NewLineForm
            {...props}
            error={error}
            s3Error={s3Error}
            loading={loading || props.isSubmitting}
            isEdit={isEdit}
          />
        )
      }}
    </Formik>
  )
}
